/**
 * The code in this file was derived from, or influenced by, TerriaJS:
 * https://github.com/TerriaJS/terriajs
 */

import MapService from 'app/shared/utilities/imagery/map_service';

type WebMapServiceItem = WebMapServiceLayer | WebMapServiceGroup;

interface WebMapServiceGroup {
    name: string;
    layers: WebMapServiceItem[];
}

interface WebMapServiceLayer {
    name: string;
    layer: string;
}

export default class WebMapService extends MapService {
    public static regex = /\/wms|\=wms/i;
    public _layers: WebMapServiceItem[];

    get url() {
        const { baseUrl, query } = this.cleanAndProxyUrl(this._url);
        return baseUrl + '?' + query.toString();
    }

    hasOptionalParameter(key: string) {
        return !!this.optionalParameters[key];
    }

    getOptionalParameter(key: string) {
        return this.optionalParameters[key];
    }

    load() {
        this._hasDataError = false;
        const { baseUrl, query } = this.cleanAndProxyUrl(this._url);
        query.set('request', 'GetCapabilities');
        const capabilitiesUrl = baseUrl + '?' + query.toString();

        return this._fetch(capabilitiesUrl)
            .then((response) => response.text())
            .then((xml) => {
                const json = this.convertXmlToJson(xml);

                // Make sure we actually have capabilities data
                if (json.WMS_Capabilities) {
                    return json.WMS_Capabilities;
                } else if (json.WMT_MS_Capabilities) {
                    return json.WMT_MS_Capabilities;
                } else {
                    throw new Error('Unable to load xml');
                }
            })
            .then((json) => {
                this._layerLimit = json.Service.LayerLimit;

                // Skip the root layer, if there's only one.
                // But use it for the name of this catalog item if the item is currently named by the URL.
                let rootLayers = json.Capability.Layer;
                if (rootLayers) {
                    if (!(rootLayers instanceof Array)) {
                        rootLayers = [rootLayers];
                    }

                    if (rootLayers.length === 1 && rootLayers[0].Layer) {
                        const singleRoot = rootLayers[0];
                        if (this._name === this._url) {
                            this._name = singleRoot.Title;
                        }
                        rootLayers = singleRoot.Layer;
                    }

                    this._addLayersRecursively(this._layers, rootLayers);
                }
            })
            .catch((error) => {
                this._hasDataError = true;
                console.error(error);
            });
    }

    private _addLayersRecursively(parentGroup, layers) {
        if (!(layers instanceof Array)) {
            layers = [layers];
        }

        for (let i = 0; i < layers.length; ++i) {
            const layer = layers[i];

            if (layer.CRS) {
                this._addCRS(layer.CRS);
            }

            if (layer.SRS) {
                this._addSRS(layer.SRS);
            }

            if (layer.Layer) {
                const group = {
                    name: layer.Title,
                    layers: [],
                };

                // WMS 1.1.1 spec section 7.1.4.5.2 says any layer with a Name property can be used
                // in the 'layers' parameter of a GetMap request.  This is true in 1.0.0 and 1.3.0 as well.
                const allName = '(All)';
                let originalNameForAll;
                if (layer.Name && layer.Name.length > 0) {
                    const all = {
                        name: layer.Title,
                        layer: layer.Name,
                    };

                    originalNameForAll = all.name;
                    all.name = allName + ' ' + all.name;
                    group.layers.push(all);
                }

                this._addLayersRecursively(group.layers, layer.Layer);

                if (group.layers.length === 1 && group.layers[0].name.indexOf(allName) === 0) {
                    group.layers[0].name = originalNameForAll;
                    parentGroup.push(group.layers[0]);
                } else if (group.layers.length > 0) {
                    parentGroup.push(group);
                }
            } else {
                parentGroup.push({
                    name: layer.Title,
                    layer: layer.Name,
                });
            }
        }
    }

    private _addCRS(crs) {
        this._addCrsOrSrs('crs', crs);
    }

    private _addSRS(srs) {
        this._addCrsOrSrs('srs', srs);
    }

    private _addCrsOrSrs(type: string, list: string | string[]) {
        const existingItems = this.optionalParameters[type]
            ? this.optionalParameters[type].map((item) => item.label)
            : [];

        if (!Array.isArray(list)) {
            list = [list];
        }

        list.forEach((item) => {
            if (!existingItems.includes(item)) {
                existingItems.push(item);
            }
        });

        this.optionalParameters[type] = existingItems.sort().map((item) => ({
            label: item,
            value: item,
        }));
    }
}
